// src/hooks/useStudentNotes.js
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../config';

export function useStudentNotes() {
  const [showNotes, setShowNotes] = useState(false);
  const [notesStudent, setNotesStudent] = useState(null);
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  const [noteToDelete, setNoteToDelete] = useState(null);

  const token = localStorage.getItem('token');

  const loadStudentNotes = async (studentId) => {
    try {
      setNotesLoading(true);
      const res = await fetch(`${API_URL}/students/${studentId}/notes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load notes');
      const data = await res.json();
      setNotes(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      toast.error('Could not load student notes');
      setNotes([]);
    } finally {
      setNotesLoading(false);
    }
  };

  const openNotesFor = async (student) => {
    setNotesStudent(student);
    setShowNotes(true);
    await loadStudentNotes(student.id);
  };

  // same idea as TeacherDashboard.handleAddStudentNote
  const addNote = async (text) => {
    if (!notesStudent) return;
    const noteText = text?.trim();
    if (!noteText) return toast.warn('Please type a note first');

    try {
      const { data } = await axios.post(
        `${API_URL}/students/${notesStudent.id}/notes`,
        { note_text: noteText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotes((prev) => [data, ...prev]);
      toast.success('Note added');
    } catch (err) {
      console.error('Add note failed', err);
      toast.error('Failed to add note');
    }
  };

  const saveNote = async () => {
    if (!notesStudent || !editingNoteId || !editingNoteText.trim()) {
      return toast.warn('Note cannot be empty');
    }

    try {
      const res = await fetch(
        `${API_URL}/students/${notesStudent.id}/notes/${editingNoteId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ note_text: editingNoteText.trim() }),
        }
      );

      if (!res.ok) throw new Error('Failed to update note');

      const updated = await res.json();

      setNotes((prev) =>
        prev.map((n) =>
          n.id === editingNoteId
            ? updated?.id
              ? updated
              : { ...n, note_text: editingNoteText }
            : n
        )
      );

      setEditingNoteId(null);
      setEditingNoteText('');
      toast.success('Note updated');
    } catch (err) {
      console.error(err);
      toast.error('Failed to update note');
    }
  };

  const deleteNote = async () => {
    if (!notesStudent || !noteToDelete) return;

    try {
      const res = await fetch(
        `${API_URL}/students/${notesStudent.id}/notes/${noteToDelete.id}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) throw new Error('Failed to delete note');

      setNotes((prev) => prev.filter((n) => n.id !== noteToDelete.id));
      setNoteToDelete(null);
      toast.success('Note deleted');
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete note');
    }
  };

  return {
    // state
    showNotes,
    setShowNotes,
    notesStudent,
    notes,
    notesLoading,
    editingNoteId,
    setEditingNoteId,
    editingNoteText,
    setEditingNoteText,
    noteToDelete,
    setNoteToDelete,
    // actions
    openNotesFor,
    addNote,
    saveNote,
    deleteNote,
  };
}
